import { useMemo } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { parse } from "qs";

import useClearQueryStringKeys from "@/hooks/useClearQueryStringKeys";
import { buildQueryPath } from "@/utils/queryPath";

const reconcileExistingValuesAndNew = (
  appliedFilterValue: string | string[],
  newFilterValue: string,
): string | string[] | undefined => {
  if (Array.isArray(appliedFilterValue)) {
    // * We've already got a collection of values.
    // * We'll add or remove the requested value, depending on whether its already present.
    return appliedFilterValue.includes(newFilterValue)
      ? appliedFilterValue.filter((v) => v !== newFilterValue)
      : [...appliedFilterValue, newFilterValue];
  } else if (appliedFilterValue === newFilterValue) {
    // * This filter exists, and is a single value equal to the requested value.
    // * This likely means the user has clicked an applied filter, and thus wishes to remove it.
    return undefined;
  } else {
    // * A filter value was found, but was different than the requested value.
    // * User is likely expanding their selection for a particular filter section.
    return [appliedFilterValue, newFilterValue];
  }
};

const getUpdatedFilterValue = (
  appliedFilterValue: string | string[] | undefined,
  newFilterValue: string,
): string | string[] | undefined => {
  if (newFilterValue === "true") {
    // * Support for toggle filters.
    return appliedFilterValue === "true" ? undefined : "true";
  } else if (appliedFilterValue) {
    return reconcileExistingValuesAndNew(appliedFilterValue, newFilterValue);
  } else {
    // * No existing filters applied, adding the requested value.
    return newFilterValue;
  }
};

const useFilterQueryParams = () => {
  const { push } = useRouter();
  const clearQueryStringKeys = useClearQueryStringKeys();
  const pathname = usePathname() || "";
  const searchParams = useSearchParams();
  const queryString = searchParams?.toString() || "";

  const { appliedFilters, nonFilterQueryParams } = useMemo(() => {
    const parsedQs = parse(queryString);
    // * Use the "filter" object in the query string by default, splitting it from other root level query string params.
    const { filter: _filter, ...nonFilterQueryParams } = parsedQs;
    // * This const should always be an record of key/values as long as the filter[] querystring params are solely modified by this hook.
    const appliedFilters =
      (parsedQs.filter as { [key: string]: string | string[] | undefined }) ||
      {};

    return {
      appliedFilters,
      nonFilterQueryParams,
    };
  }, [queryString]);

  const getUrlWithNewFilter = (name: string, newFilterValue: string) => {
    const { [name]: appliedFilterValue, ...otherFilters } = appliedFilters;

    const filterValue = getUpdatedFilterValue(
      appliedFilterValue,
      newFilterValue,
    );

    const { page: _page, ...nonFilterOrPageQueryParams } = nonFilterQueryParams;

    const urlWithNewFilter = buildQueryPath(pathname, {
      ...nonFilterOrPageQueryParams,
      filter: {
        ...otherFilters,
        [name]: filterValue,
      },
    });

    return urlWithNewFilter;
  };

  const toggleFilter = (name: string, newFilterValue: string) =>
    push(getUrlWithNewFilter(name, newFilterValue), { scroll: false });

  const clearAllFilters = () => {
    clearQueryStringKeys(["filter", "page"]);
  };

  return {
    actions: {
      clearAllFilters,
      toggleFilter,
    },
    appliedFilters,
    utils: {
      getUrlWithNewFilter,
    },
  };
};

export default useFilterQueryParams;
